import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Loader2, Sparkles, FolderOpen, Bot } from 'lucide-react'
import { industryPresetsService, type IndustryPresetSummary } from '@/lib/api/services/industryPresets'

interface WorkspaceSetupStepProps {
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

export function WorkspaceSetupStep({ onSubmit, disabled }: WorkspaceSetupStepProps) {
  const [setupType, setSetupType] = useState<'preset' | 'scratch'>('preset')
  const [presets, setPresets] = useState<IndustryPresetSummary[]>([])
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null)
  const [includeDefaultAgents, setIncludeDefaultAgents] = useState(true)
  const [loading, setLoading] = useState(true)
  const [presetsError, setPresetsError] = useState(false)

  useEffect(() => {
    loadPresets()
  }, [])

  const loadPresets = async () => {
    try {
      const data = await industryPresetsService.getAll()
      setPresets(data)
      setPresetsError(false)
      // Auto-select featured preset
      const featured = data.find(p => p.isFeatured)
      if (featured) {
        setSelectedPresetId(featured.id)
      }
    } catch (error) {
      console.error('Failed to load presets:', error)
      setPresetsError(true)
      // Default to 'scratch' mode when presets can't be loaded
      setSetupType('scratch')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => {
    onSubmit({
      setupType,
      presetId: setupType === 'preset' ? selectedPresetId : null,
      includeDefaultAgents
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          Set up your workspace
        </h2>
        <p className="text-slate-600">
          Choose how you'd like to get started
        </p>
      </div>

      {/* Setup type selection */}
      <div className="grid gap-4 sm:grid-cols-2 max-w-2xl mx-auto">
        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            setupType === 'preset' ? 'ring-2 ring-primary' : ''
          } ${presetsError ? 'opacity-60' : ''}`}
          onClick={() => !presetsError && setSetupType('preset')}
        >
          <CardHeader className="text-center">
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
              setupType === 'preset' ? 'bg-primary text-primary-foreground' : 'bg-slate-100'
            }`}>
              <Sparkles className="h-6 w-6" />
            </div>
            <CardTitle className="text-lg">Start with a preset</CardTitle>
            <CardDescription>
              {presetsError
                ? 'Industry presets are temporarily unavailable'
                : 'Get pre-configured agents, templates, and workflows for your industry'}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card
          className={`cursor-pointer transition-all hover:shadow-md ${
            setupType === 'scratch' ? 'ring-2 ring-primary' : ''
          }`}
          onClick={() => setSetupType('scratch')}
        >
          <CardHeader className="text-center">
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
              setupType === 'scratch' ? 'bg-primary text-primary-foreground' : 'bg-slate-100'
            }`}>
              <FolderOpen className="h-6 w-6" />
            </div>
            <CardTitle className="text-lg">Start from scratch</CardTitle>
            <CardDescription>
              Build your workspace with a clean slate
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      {/* Preset selection */}
      {setupType === 'preset' && presets.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-slate-900 text-center">
            Select an industry preset
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
            {presets.slice(0, 6).map((preset) => (
              <Card
                key={preset.id}
                className={`cursor-pointer transition-all hover:shadow-sm ${
                  selectedPresetId === preset.id ? 'ring-2 ring-primary bg-primary/5' : ''
                }`}
                onClick={() => setSelectedPresetId(preset.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-lg"
                      style={{ backgroundColor: preset.color }}
                    >
                      {preset.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{preset.name}</h4>
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {preset.agentCount} agents, {preset.templateCount} templates
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Default agents toggle */}
      <div className="max-w-md mx-auto pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="h-5 w-5 text-slate-500" />
            <div>
              <Label htmlFor="defaultAgents" className="font-medium">
                Include default AI agents
              </Label>
              <p className="text-sm text-slate-500">
                Get started with 6 pre-configured AI assistants
              </p>
            </div>
          </div>
          <Switch
            id="defaultAgents"
            checked={includeDefaultAgents}
            onCheckedChange={setIncludeDefaultAgents}
          />
        </div>
      </div>

      <div className="flex justify-center pt-4">
        <Button
          onClick={handleSubmit}
          disabled={disabled || (setupType === 'preset' && !selectedPresetId)}
          size="lg"
        >
          {disabled ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </div>
  )
}
